import { http } from '@/lib/http';
import type { Decimal, IdResponse, RowVersion, UtcDateTime } from './types';

export type TenantKind = 'SYSTEM' | 'CUSTOMER' | 'DEMO';

export interface Me {
  id: number;
  tenantId: number;
  tenantCode: string;
  tenantName: string;
  /** SYSTEM = Nguyên Bình's platform operator tenant (platform admins only). */
  tenantKind: TenantKind | string;
  /** Nguyên Bình platform admin (SYSTEM tenant). Grants only `platform.tenants.manage`. */
  isPlatformAdmin: boolean;
  email: string;
  displayName: string;
  jobTitle: string | null;
  roles: string[];
  permissions: string[];
  /** Signed avatar URL (usable in <img src>), null = no photo → initials. */
  avatarUrl?: string | null;
}

export interface SessionResponse {
  accessToken: string;
  accessTokenExpiresAtUtc: UtcDateTime;
  refreshToken: string;
  user: Me;
}

export interface LoginRequest {
  /** Optional: only sent after the server answered 409 TENANT_SELECTION_REQUIRED (or from `?tenant=`). */
  tenantCode?: string;
  email: string;
  password: string;
}

/** One entry of the 409 TENANT_SELECTION_REQUIRED body (`tenants`): tenants where the password matched. */
export interface TenantChoice {
  code: string;
  name: string;
}

export interface User {
  id: number;
  email: string;
  displayName: string;
  jobTitle: string | null;
  phone: string | null;
  status: 'ACTIVE' | 'DISABLED' | string;
  lastLoginAtUtc: UtcDateTime | null;
  /** Set only while a sign-in lockout (too many failed passwords) is running. */
  lockedUntilUtc?: UtcDateTime | null;
  roleIds: number[];
  roleCodes: string[];
  version: RowVersion;
  avatarUrl?: string | null;
}

export interface UserLookup {
  id: number;
  displayName: string;
  jobTitle: string | null;
  avatarUrl?: string | null;
}

/** PUT/DELETE …/avatar → the new signed URL (null after removal) and the user's new row version. */
export interface AvatarResponse {
  avatarUrl: string | null;
  version: RowVersion;
}

/** Client-side mirror of the server rule (the server re-checks magic bytes and re-encodes to a 256px WebP). */
export const AVATAR_MAX_BYTES = 2 * 1024 * 1024;
export const AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

/** GET /identity/roles/lookup — code + name only (policy editors without roles.manage). */
export interface RoleLookup {
  code: string;
  name: string;
}

export interface CreateUserRequest {
  email: string;
  displayName: string;
  jobTitle?: string | null;
  phone?: string | null;
  password: string;
  roleIds: number[];
}

export interface UpdateUserRequest {
  displayName: string;
  jobTitle?: string | null;
  phone?: string | null;
  status: string;
  version: RowVersion;
}

export interface Role {
  id: number;
  code: string;
  name: string;
  isSystem: boolean;
  permissions: string[];
  userCount: number;
}

export interface Permission {
  code: string;
  module: string;
  description: string;
}

export interface Team {
  id: number;
  code: string;
  name: string;
  managerUserId: number | null;
  memberIds: number[];
}

export interface UpsertTeamRequest {
  code: string;
  name: string;
  managerUserId: number | null;
  memberIds: number[];
}

export interface Delegation {
  id: number;
  delegatorId: number;
  delegatorName: string;
  delegateId: number;
  delegateName: string;
  requestTypeCodes: string[];
  amountLimit: Decimal | null;
  validFromUtc: UtcDateTime;
  validToUtc: UtcDateTime;
  reason: string;
  status: 'ACTIVE' | 'REVOKED' | string;
  isEffective: boolean;
  delegatorAvatarUrl?: string | null;
  delegateAvatarUrl?: string | null;
}

export interface CreateDelegationRequest {
  delegatorId?: number | null;
  delegateId: number;
  requestTypeCodes: string[];
  amountLimit: Decimal | null;
  validFromUtc: UtcDateTime;
  validToUtc: UtcDateTime;
  reason: string;
}

const base = '/identity';

function avatarForm(file: File): FormData {
  const form = new FormData();
  form.append('file', file, file.name);
  return form;
}

export const identityApi = {
  login: (req: LoginRequest) => http.post<SessionResponse>(`${base}/sessions`, req, { anonymous: true }),
  logout: (refreshToken: string) => http.post<void>(`${base}/sessions/logout`, { refreshToken }, { anonymous: true }),
  me: () => http.get<Me>(`${base}/me`),

  lookupUsers: (q?: string) => http.get<UserLookup[]>(`${base}/users/lookup`, { q }),
  /** Names for ids (any signed-in user; includes disabled users). Max 200 ids per call. */
  usersByIds: (ids: readonly number[]) => http.get<UserLookup[]>(`${base}/users/by-ids`, { ids: ids.join(',') }),
  rolesLookup: () => http.get<RoleLookup[]>(`${base}/roles/lookup`),
  listUsers: () => http.get<User[]>(`${base}/users`),
  createUser: (req: CreateUserRequest) => http.post<IdResponse>(`${base}/users`, req),
  updateUser: (id: number, req: UpdateUserRequest) => http.patch<void>(`${base}/users/${id}`, req),
  setUserRoles: (id: number, roleIds: number[]) => http.put<void>(`${base}/users/${id}/roles`, { roleIds }),
  resetPassword: (id: number, newPassword: string) => http.post<void>(`${base}/users/${id}/password-resets`, { newPassword }),

  /** Own avatar (any signed-in user). */
  setMyAvatar: (file: File) => http.put<AvatarResponse>(`${base}/me/avatar`, avatarForm(file)),
  removeMyAvatar: () => http.delete<AvatarResponse>(`${base}/me/avatar`),
  /** Another user's avatar (identity.users.manage). */
  setUserAvatar: (id: number, file: File) => http.put<AvatarResponse>(`${base}/users/${id}/avatar`, avatarForm(file)),
  removeUserAvatar: (id: number) => http.delete<AvatarResponse>(`${base}/users/${id}/avatar`),

  listPermissions: () => http.get<Permission[]>(`${base}/permissions`),
  listRoles: () => http.get<Role[]>(`${base}/roles`),
  createRole: (code: string, name: string) => http.post<IdResponse>(`${base}/roles`, { code, name }),
  setRolePermissions: (id: number, permissionCodes: string[]) => http.put<void>(`${base}/roles/${id}/permissions`, { permissionCodes }),

  listTeams: () => http.get<Team[]>(`${base}/teams`),
  createTeam: (req: UpsertTeamRequest) => http.post<IdResponse>(`${base}/teams`, req),
  updateTeam: (id: number, req: UpsertTeamRequest) => http.put<IdResponse>(`${base}/teams/${id}`, req),

  listDelegations: () => http.get<Delegation[]>(`${base}/delegations`),
  createDelegation: (req: CreateDelegationRequest, idempotencyKey?: string) =>
    http.post<IdResponse>(`${base}/delegations`, req, { idempotencyKey }),
  revokeDelegation: (id: number) => http.post<void>(`${base}/delegations/${id}/revocations`),
};
